import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Play, Trophy, Users, Calendar, Flag, BarChart3 } from "lucide-react";

export default function RoundDetail() {
  const navigate = useNavigate();
  const { roundId } = useParams();
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRound = useCallback(async () => {
    try {
      const response = await fetch(`${API}/rounds/${roundId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setRound(data);
      } else {
        toast.error("Ronda no encontrada");
        navigate("/dashboard");
      }
    } catch {
      toast.error("Error al cargar la ronda");
    } finally {
      setLoading(false);
    }
  }, [roundId, navigate]);

  useEffect(() => {
    fetchRound();
  }, [fetchRound]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const basePlayers = round?.players?.filter(p => p.is_base) || [];
  const secondaryPlayers = round?.players?.filter(p => !p.is_base) || [];
  const completedHoles = round?.players?.[0]?.scores?.length || 0;

  // Calculate totals
  let totalBasePoints = 0;
  round?.matchups?.forEach(m => {
    totalBasePoints += m.total_points;
  });

  // Calculate total units per player
  const totalUnits = {};
  round?.players?.forEach(player => {
    totalUnits[player.player_id] = player.scores?.reduce((sum, s) => sum + (s.unit_count || 0), 0) || 0;
  });

  const handleContinuePlay = () => {
    if (round?.status === "setup") {
      navigate(`/round/${roundId}/players`);
    } else if (round?.status === "in_progress") {
      navigate(`/round/${roundId}/play`);
    } else {
      navigate(`/round/${roundId}/results`);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[#0F172A]">{round?.name}</h1>
            <p className="text-xs text-[#64748B]">{round?.course}</p>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Round Info */}
        <Card className="card-default mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-[#64748B]" />
                <span className="text-sm text-[#0F172A]">{round?.date}</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                round?.status === "completed" 
                  ? "bg-green-100 text-green-700" 
                  : round?.status === "in_progress"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-yellow-100 text-yellow-700"
              }`}>
                {round?.status === "completed" ? "Completada" : round?.status === "in_progress" ? `Hoyo ${completedHoles}/18` : "Configurando"}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Players */}
        {round?.players?.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-[#64748B] mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Jugadores
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {round.players.map(player => (
                <div 
                  key={player.player_id}
                  className={`p-3 rounded-lg ${player.is_base ? 'bg-[#1B4D3E]/5 border border-[#1B4D3E]/10' : 'bg-[#D4A373]/10 border border-[#D4A373]/20'}`}
                >
                  <p className="font-medium text-sm text-[#0F172A]">{player.name}</p>
                  <p className="text-xs text-[#64748B]">Hcp: {player.handicap} {player.is_base && '• Base'}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Matchups Summary */}
        {round?.matchups?.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-[#64748B] mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Enfrentamientos
            </h3>
            <div className="space-y-2">
              {round.matchups.map((matchup, index) => {
                const secPlayers = matchup.secondary_pair.map(id => 
                  round.players.find(p => p.player_id === id)
                );
                return (
                  <Card key={matchup.matchup_id || `matchup-${index}`} className="card-default">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm">
                          <span className="text-[#64748B]">vs </span>
                          <span className="font-medium text-[#0F172A]">
                            {secPlayers.map(p => p?.name?.split(' ')[0]).join(' + ')}
                          </span>
                        </div>
                        <span className={`font-bold ${
                          matchup.total_points > 0 ? 'text-green-600' : matchup.total_points < 0 ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {matchup.total_points > 0 ? '+' : ''}{matchup.total_points}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Units Summary */}
        {Object.values(totalUnits).some(v => v > 0) && (
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-[#64748B] mb-3 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-[#D4A373]" />
              Unidades
            </h3>
            <Card className="card-default">
              <CardContent className="p-3">
                <div className="flex flex-wrap gap-3">
                  {round?.players?.filter(p => totalUnits[p.player_id] > 0).map(player => (
                    <div key={player.player_id} className="flex items-center gap-2 bg-[#D4A373]/10 px-2 py-1 rounded">
                      <span className="text-sm text-[#0F172A]">{player.name.split(' ')[0]}</span>
                      <span className="font-bold text-[#D4A373]">{totalUnits[player.player_id]}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Action Button */}
        <Button
          onClick={handleContinuePlay}
          className="btn-primary w-full py-6"
          data-testid="action-btn"
        >
          {round?.status === "completed" ? (
            <>
              <Trophy className="w-5 h-5 mr-2" />
              Ver Resultados
            </>
          ) : round?.status === "in_progress" ? (
            <>
              <Play className="w-5 h-5 mr-2" />
              Continuar Jugando
            </>
          ) : (
            <>
              <Users className="w-5 h-5 mr-2" />
              Configurar Jugadores
            </>
          )}
        </Button>
      </main>
    </div>
  );
}
